import { forwardRef, useState } from 'react';
import {
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { validateData, type ErrorObject } from '../../providers/validateData';
import CustomTypography from '../Typography/CustomTypography';

export interface Option {
  label: string;
  value: string;
}

interface ValidationError {
  validateRules?: 'required'[];
  onValidationError?: (error: ErrorObject | null) => void;
}

interface CustomCheckboxGroupProps extends ValidationError {
  id: string;
  options: Option[];
  selectedValues?: string[];
  className?: string;
  label?: string;
  onChange: (selected: string[]) => void;
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  size?: 'small' | 'medium';
  disabled?: boolean;
  row?: boolean;
  singleSelect?: boolean;
}

const CustomCheckboxGroup = forwardRef<
  HTMLInputElement,
  CustomCheckboxGroupProps
>(
  (
    {
      id,
      options,
      selectedValues = [],
      className,
      label,
      onChange,
      color = 'primary',
      size = 'small',
      disabled = false,
      row = false,
      singleSelect = false,
      validateRules,
      onValidationError,
    },
    ref
  ) => {
    const [selected, setSelected] = useState<string[]>(selectedValues);
    const [inputError, setInputError] = useState<ErrorObject | null>(null);

    const handleCheckboxChange = (value: string) => {
      let updatedSelected: string[];
      if (selected.includes(value)) {
        updatedSelected = singleSelect
          ? []
          : selected.filter((item) => item !== value);
      } else {
        updatedSelected = singleSelect ? [value] : [...selected, value];
      }

      setSelected(updatedSelected);
      onChange(updatedSelected);

      if (validateRules) {
        const errors = validateData(updatedSelected, validateRules);
        if (onValidationError) {
          onValidationError(errors);
        }
        setInputError(errors);
      }
    };

    return (
      <FormControl component="fieldset">
        {label && <FormLabel component="legend">{label}</FormLabel>}
        <FormGroup row={row}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              className={className}
              sx={{
                // Space between checkbox and label
                '& .MuiFormControlLabel-label': {
                  marginLeft: '8px', // Adjust spacing here
                },
              }}
              control={
                <Checkbox
                  checked={selected.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  color={color}
                  size={size}
                  disabled={disabled}
                  inputRef={ref}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
        {inputError?.invalid && (
          <CustomTypography id={`${id}-error`} variant="h6" color="error">
            {inputError.message}
          </CustomTypography>
        )}
      </FormControl>
    );
  }
);

CustomCheckboxGroup.displayName = 'CustomCheckboxGroup';

export default CustomCheckboxGroup;
